import React, { useEffect, useRef, useState } from 'react';
import classes from './styles.module.scss';
import { XIcon } from '../../assets';
import { useOutsideClick } from '../../hooks/useOutsideClick.hook';

type ModalProps = {
  show: boolean;
  children: React.ReactNode;
  handleClose: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  setShow?: (value: boolean) => void;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
  style?: React.CSSProperties;
  modalStyle?: React.CSSProperties;
  hideClose?: boolean;
  animated?: boolean;
};

const CLOSE_MS = 180;

const Modal: React.FC<ModalProps> = ({
  onScroll,
  children,
  handleClose,
  show,
  header,
  footer,
  style,
  modalStyle,
  hideClose = false,
  animated = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(show);
  const [isClosing, setIsClosing] = useState(false);

  const requestClose = () => {
    if (animated) {
      setIsClosing(true);
      window.setTimeout(() => {
        setIsClosing(false);
        setShouldRender(false);
        handleClose();
      }, CLOSE_MS);
      return;
    }
    handleClose();
  };

  useOutsideClick(modalRef, requestClose);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender && animated) {
      setIsClosing(true);
      const timer = window.setTimeout(() => {
        setIsClosing(false);
        setShouldRender(false);
      }, CLOSE_MS);
      return () => window.clearTimeout(timer);
    } else if (!show) {
      setShouldRender(false);
    }
  }, [show, animated, shouldRender]);

  useEffect(() => {
    if (shouldRender) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldRender]);

  if (!shouldRender) {
    return null;
  }

  const contentClass = [
    classes['modal-content'],
    animated ? (isClosing ? classes['modal-content--exit'] : classes['modal-content--enter']) : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={[classes['modal'], animated ? classes['modal--animated'] : ''].filter(Boolean).join(' ')}
      style={modalStyle}
    >
      <div style={style} className={contentClass} ref={modalRef} onScroll={onScroll}>
        {header && <div className={classes['modal-header']}>{header}</div>}
        {children}
        {!hideClose && (
          <span className={classes['modal-close']} onClick={requestClose}>
            <XIcon />
          </span>
        )}
        {footer && <div className={classes['modal-footer']}>{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
